/** @jsx jsx */

import { ComponentProps, ElementType, ReactNode } from 'react';

import { Icon } from '@zapier/zinnia-icons';
import {
  Colors,
  Grid,
  Text,
  Tooltip,
  TooltipFocusableTrigger,
} from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

import ExtendedIcon from 'app/developer-v3/components/ExtendedIcon';
import Link from 'app/common/components/Link';

const Styles = {
  root: (hasLeftIndent: boolean) => css`
    cursor: pointer;
    padding-left: ${hasLeftIndent ? '24px' : '0px'};
  `,
  disabled: css`
    cursor: not-allowed;
    color: ${Colors.GrayWarm7};
    :hover {
      background-color: transparent;
      color: ${Colors.GrayWarm7};
    }
  `,
  linkContent: isActive => [
    css`
      width: 100%;
      border-radius: 3px;
      padding: 10px;
      :hover {
        background-color: ${Colors.GrayWarm2};
      }
    `,
    isActive &&
      css`
        background-color: ${Colors.UiPrimaryWeakest};
        :hover {
          background-color: ${Colors.UiPrimaryWeakest};
        }
      `,
  ],
};

export type Props = {
  /** Apply a badge after the submenu item */
  badge?: ReactNode;
  /** Apply a diferent column layout. Useful when children has more than one child */
  gridTemplateColumn?: string;
  /** Should indent from the left */
  hasLeftIndent: boolean;
  /** The URL clicking the link will direct the user to. */
  href: string;
  /** The type of icon to render in front of the link text. */
  icon?: ComponentProps<typeof ExtendedIcon>['name'];
  /** Is this the current active link. */
  isActive?: boolean;
  /** Is this link locked/disabled */
  isLocked?: boolean;
  /** Label */
  label: string;
  /** Callback when clicked */
  onClick: () => void;
  tag?: ElementType;
  /** Target of this link */
  target?: ComponentProps<typeof Link>['target'];
  /** Tooltip */
  tooltip?: string;
};

/**
 * Renders a NavLink to be used with this component.
 * Display colors vary depending on whether the link is active or disabled.
 */
function NavLink({
  badge,
  gridTemplateColumn,
  hasLeftIndent,
  href,
  label,
  icon,
  isLocked,
  isActive,
  onClick,
  tag: Tag = 'li',
  target,
  tooltip,
}: Props) {
  const hasIcon = !!(icon || isLocked);

  const link = (
    <Link href={isLocked ? undefined : href} onPress={onClick} target={target}>
      <div css={[Styles.linkContent(isActive), isLocked && Styles.disabled]}>
        <Grid
          alignItems="center"
          columns={
            gridTemplateColumn
              ? gridTemplateColumn
              : isLocked
              ? '20px auto'
              : 'auto 1fr'
          }
          gap={isLocked ? 5 : 10}
        >
          {hasIcon &&
            (isLocked ? (
              <Icon name="actionLockFill" size={20} />
            ) : (
              <ExtendedIcon name={icon} size={20} />
            ))}
          <Text fontWeight={isActive ? 600 : 400}>{label}</Text>
          <Text>{badge}</Text>
        </Grid>
      </div>
    </Link>
  );

  return (
    <Tag css={[Styles.root(hasLeftIndent), isLocked && Styles.disabled]}>
      {tooltip ? (
        <Tooltip content={tooltip} placement="bottom">
          <TooltipFocusableTrigger>{link}</TooltipFocusableTrigger>
        </Tooltip>
      ) : (
        link
      )}
    </Tag>
  );
}

export default NavLink;
